import { useTranslation } from "react-i18next";
import { Lightbulb } from "lucide-react";
import { lt } from "../localize";
import type { LessonStage } from "../types";

interface StageRevealProps {
  stage: LessonStage;
}

/**
 * Split content text: first sentence is highlighted, rest is muted.
 * Handles `.`, `!`, and `?` as sentence terminators.
 */
function splitFirstSentence(text: string): { first: string; rest: string } {
  const match = text.match(/^(.+?[.!?])\s*([\s\S]*)$/);
  if (match) {
    return { first: match[1], rest: match[2] };
  }
  return { first: text, rest: "" };
}

export const StageReveal = ({ stage }: StageRevealProps) => {
  const { i18n } = useTranslation();
  const lang = i18n.language;

  const contentText = lt(stage.content, lang);
  const { first, rest } = splitFirstSentence(contentText);

  return (
    <div className="space-y-4 flex flex-col justify-center min-h-[300px]">
      {/* Title with sparkle */}
      <h2
        className="text-xl font-bold"
        style={{ color: "hsl(var(--level-accent))" }}
      >
        {lt(stage.title, lang)} <span aria-hidden="true">&#10024;</span>
      </h2>

      {/* Reveal card */}
      <div className="glass relative overflow-hidden">
        {/* Gradient overlay at top */}
        <div
          className="absolute top-0 left-0 right-0 h-20 pointer-events-none"
          style={{
            background: "linear-gradient(to bottom, hsl(var(--level-accent) / 0.1), transparent)",
          }}
        />

        <div className="relative p-5">
          <div className="flex items-start gap-3">
            {/* Animated lightbulb in colored circle */}
            <div
              className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center"
              style={{
                backgroundColor: "hsl(var(--level-accent) / 0.2)",
                color: "hsl(var(--level-accent))",
                animation: "glowPulse 2s ease-in-out infinite",
              }}
            >
              <Lightbulb className="w-4.5 h-4.5" />
            </div>

            {/* Content with highlighted first sentence */}
            <p className="text-sm leading-relaxed">
              <strong className="text-foreground">{first}</strong>
              {rest && (
                <span className="text-muted-foreground"> {rest}</span>
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
